import ChevronRight from '@atom-web/icons-24/ChevronRight'
import { Box, Column, Row, Text } from '@atom-web/micros/dist/'
import forIn from 'lodash/forIn'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { AppConfig } from '../../../app-config'
import ContentWrapper from '../../../components/ContentWrapper'
import GCMContainer from '../../packages/gcm/GCMContainer'
import CategorySlider from './components/CategorySlider'
import FaqPanel from './components/FaqPanel'
import FlashDeal from './components/FlashDeal'
import HeroSection from './components/HeroSection'
import PriceFilterPanel from './components/PriceFilterPanel'

const MaldivesLandingPage = ({ data, isMobile }) => {
	const router = useRouter()
	const { query } = router

	const categoryPanelData = useMemo(() => {
		const arr = []
		const { tagResortMap = {}, testimonials = [] } = data
		let index = 0
		forIn(tagResortMap, ({ tag, resorts }, key) => {
			if (resorts?.length) {
				let obj = {
					name: tag?.name,
					description: tag?.description,
					icon: tag?.icon,
					resorts: [...resorts, resorts?.[2]],
					testimonial: testimonials?.[index],
					key,
					bgPosition: '0 0',
				}
				if (key?.includes('honeymoon')) {
					obj.bgPosition = '-64px 0'
				} else if (key?.includes('overwater-villa')) {
					obj.bgPosition = '-160px 0'
				} else if (key?.includes('kid-friendly')) {
					obj.bgPosition = '-96px 0'
				} else if (key?.includes('seaplane')) {
					obj.bgPosition = '-128px 0'
				} else if (key?.includes('great-indian-food')) {
					obj.bgPosition = '-32px 0'
				}
				arr.push(obj)
			}
			index++
		})
		return arr
	}, [data])

	const assetBg = useMemo(
		() =>
			'url(https://pyt-images.imgix.net/images/web_app/speed-boat/mle_sprites_v1.png) no-repeat',
		[],
	)
	const bgSize = useMemo(() => '650px 600px', [])

	const showGetCostModal = useCallback(
		({ key, price }) => {
			if (query.m !== 'mle-landing') {
				const query = {
					m: 'mle-landing',
				}
				if (key) query.key = key
				if (price) query.price = price
				router.push(
					{
						pathname: router.asPath.split('?')[0],
						query,
					},
					null,
					{ shallow: true, scroll: false },
				)
			}
		},
		[router],
	)

	return (
		<>
			<Head>
				<title>Best Resorts in Maldives | Pickyourtrail</title>
				<meta
					name={'description'}
					content={`Discover all inclusive Maldives resorts for your dream vacation on a tropical paradise.
					Pickyourtrail offers a wide range of resorts available at the Maldives ranging from beach villas,
					water villas, in-ocean villa and resorts for a luxurious holiday.`}
				/>
				<link
					rel="canonical"
					href={`${AppConfig.domain_url}packages/maldives`}
				/>
				<meta
					property="og:title"
					content="Best Resorts in Maldives | Pickyourtrail"
				/>
				<meta
					property="og:description"
					content={`Discover all inclusive Maldives resorts for your dream vacation on a tropical paradise.
              Pickyourtrail offers a wide range of resorts available at the Maldives ranging from beach villas,
              water villas, in-ocean villa and resorts for a luxurious holiday.`}
				/>
				<meta
					property="og:image"
					content={
						`https://pyt-images.imgix.net/images/web_app/speed-boat/video/mle-poster.jpg` ||
						null
					}
				/>
				<meta
					property="og:url"
					content={`${AppConfig.domain_url}packages/maldives`}
				/>
				<meta property="og:type" content="website" />
			</Head>

			<Column
				css={{
					paddingBottom: '40px',
					scrollBehavior: 'smooth',
				}}
			>
				{/* Hero section */}
				<HeroSection data={data} isMobile={isMobile} />

				{/* flash deal section */}
				<Column
					css={{
						position: 'relative',
						backgroundColor: '#f4f9f8',

						'&::before': {
							position: 'absolute',
							content: '',
							top: 0,
							right: 0,
							background:
								'url(https://d3lf10b5gahyby.cloudfront.net/web_app/flash_deals.png) no-repeat',
							width: '104px',
							height: '66px',
							zIndex: 99,

							'@smMax': {
								display: 'none',
							},
						},
						'&::after': {
							position: 'absolute',
							content: '',
							bottom: 0,
							left: 0,
							background:
								'url(https://d3lf10b5gahyby.cloudfront.net/web_app/flash_deals.png) no-repeat',
							width: '104px',
							height: '66px',
							zIndex: 99,

							'@smMax': {
								display: 'none',
							},
						},
					}}
				>
					<ContentWrapper>
						<Column
							css={{
								padding: '64px',

								'@smMax': {
									padding: '40px 0 0 0',
								},
							}}
						>
							<FlashDeal
								data={data?.flashDealResorts}
								displayCurrency={data?.displayCurrency}
							/>
						</Column>
					</ContentWrapper>
				</Column>

				{/* category panel */}
				<ContentWrapper>
					<Column
						gap={7}
						css={{
							padding: '32px 64px 32px 64px',
							'@smMax': {
								padding: 0,
							},
						}}
					>
						{categoryPanelData?.map(
							(
								{ name, description, resorts, testimonial, bgPosition, key },
								index,
							) => (
								<Column
									gap={2}
									key={'category-panel-' + index}
									css={{
										width: '100%',
									}}
								>
									{index === 2 && (
										<PriceFilterPanel
											assetBg={assetBg}
											bgSize={bgSize}
											onClickPriceFilter={route =>
												showGetCostModal({ price: route })
											}
										/>
									)}
									{index === 3 && (
										<FaqPanel assetBg={assetBg} bgSize={bgSize} />
									)}
									<Row
										gap={2}
										align="center"
										css={{
											'@smMax': {
												flexDirection: 'row-reverse',
												justifyContent: 'flex-end',
												paddingBottom: '$3',
											},
										}}
									>
										<Row
											align="center"
											css={{
												marginLeft: 'auto',
												'@lg': {
													display: 'none',
												},
											}}
											onClick={() =>
												showGetCostModal({
													key,
												})
											}
										>
											<Text
												css={{
													fontSize: '14px',
													fontWeight: '$bold',
													lineHeight: '18px',
													color: '#1895b3',
												}}
											>
												VIEW ALL
											</Text>
											<ChevronRight fill="#1895b3" />
										</Row>
										<Text
											as="h2"
											css={{
												fontSize: '24px',
												lineHeight: '32px',
												color: 'rgba(0,0,0,.9)',
												letterSpacing: '-0.03em',

												'@smMax': {
													fontSize: '17px',
												},
											}}
										>
											{name}
										</Text>
										<Box
											css={{
												width: '32px',
												height: '32px',
												background: assetBg,
												backgroundSize: bgSize,
												backgroundPosition: bgPosition,
											}}
										/>
									</Row>
									<Row
										justify="between"
										css={{
											'@smMax': {
												display: 'none',
											},
										}}
									>
										<Text
											as="p"
											css={{
												fontSize: '17px',
												lineHeight: 1.41,
												color: '#777',
												letterSpacing: '-0.25px',
												paddingBottom: '$3',
											}}
										>
											{description}
										</Text>
										<Row
											gap={1}
											align="center"
											css={{
												color: '#1895b3',
												cursor: 'pointer',

												'&:hover': {
													color: '#148099',
												},
											}}
											onClick={() =>
												showGetCostModal({
													key,
												})
											}
										>
											<Text
												css={{
													fontSize: '17px',
													fontWeight: '$bold',
												}}
											>
												VIEW ALL RESORTS
											</Text>
											<ChevronRight fill="#1895b3" width={17} height={17} />
										</Row>
									</Row>

									<CategorySlider
										resorts={resorts}
										testimonial={testimonial}
										displayCurrency={data?.displayCurrency}
									/>
								</Column>
							),
						)}
					</Column>
				</ContentWrapper>
			</Column>
			<GCMContainer />
		</>
	)
}

export default MaldivesLandingPage
